import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, formatApiError, ROLE_LABEL, ROLE_COLOR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { id: null, full_name: "", username: "", password: "", role: "REPRESENTATIVE", permissions: [] };

export default function StaffPage() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ permissions: {}, defaults: {} });
  const [form, setForm] = useState(null);

  const load = async () => {
    const [{ data }, { data: m }] = await Promise.all([api.get("/admin/staff"), api.get("/admin/permissions")]);
    setItems(data);
    setMeta(m);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setForm({ ...EMPTY, permissions: meta.defaults.REPRESENTATIVE || [] });
  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) {
        await api.put(`/admin/staff/${form.id}`, { full_name: form.full_name, password: form.password || null, permissions: form.permissions });
        toast.success("Hesap güncellendi");
      } else {
        await api.post("/admin/staff", form);
        toast.success("Yetkili hesap oluşturuldu");
      }
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hesap silinsin mi?")) return;
    await api.delete(`/admin/staff/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Süper Admin</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Yetkili Hesaplar</h1>
          <p className="text-slate-500 mt-1">Temsilci ve platform muhasebe hesapları ile erişim izinleri.</p>
        </div>
        <Button data-testid="add-staff-btn" onClick={openNew} className="bg-slate-900"><Plus className="w-4 h-4 mr-2" />Yeni Hesap</Button>
      </div>

      <Card className="divide-y">
        {items.length === 0 && <div className="p-10 text-center text-slate-400">Henüz yetkili hesap yok.</div>}
        {items.map((s) => (
          <div key={s.id} data-testid={`staff-row-${s.username}`} className="p-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold">{s.full_name} <span className="text-xs text-slate-500">@{s.username}</span></div>
              <Badge variant="outline" className={`mt-1 ${ROLE_COLOR[s.role]}`}>{ROLE_LABEL[s.role]}</Badge>
              <div className="flex flex-wrap gap-1 mt-2">
                {(s.permissions || []).map((p) => <Badge key={p} variant="secondary" className="text-[10px]">{meta.permissions[p] || p}</Badge>)}
              </div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" data-testid={`edit-staff-${s.username}`} onClick={() => setForm({ ...s, password: "" })}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" data-testid={`delete-staff-${s.username}`} onClick={() => remove(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{form?.id ? "Hesabı Düzenle" : "Yeni Yetkili Hesap"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-3">
              {!form.id && (
                <div>
                  <Label>Hesap Türü</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, permissions: meta.defaults[v] || [] })}>
                    <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="REPRESENTATIVE">Temsilci</SelectItem>
                      <SelectItem value="PLATFORM_ACCOUNTANT">Platform Muhasebe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Ad Soyad *</Label><Input data-testid="staff-fullname" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kullanıcı Adı *</Label><Input data-testid="staff-username" required disabled={!!form.id} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
                <div><Label>{form.id ? "Yeni Şifre" : "Şifre *"}</Label><Input data-testid="staff-password" type="password" required={!form.id} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={form.id ? "Değişmeyecekse boş bırakın" : ""} /></div>
              </div>
              <div className="pt-2 border-t space-y-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Erişim İzinleri</div>
                {Object.entries(meta.permissions).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`perm-${k}`} checked={form.permissions.includes(k)} onCheckedChange={() => togglePerm(k)} /> {label}
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>İptal</Button>
                <Button data-testid="staff-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
